import { useEffect, useMemo, useState } from "react";

import { snapshotToProtobuf } from "@optimusride/client-library/utils/firestore";
import { RouteProgress } from "@optimusride/client-library/grpc/route_progress_pb";
import { db, firebase } from "@optimusride/client-library/utils/firebase";
import { RouteProgressStatus } from "@optimusride/client-library/@types/grpc/route-management";
import { Coordinates } from "@optimusride/client-library/@types/grpc/common";
import { DriveGear } from "@optimusride/client-library/@types/grpc/vehicle_status";
import { BLUE_300 } from "../../common/styles/colors";
import { convertLatLng } from "../../common/utils";

const VEHICLE_ROUTE_PROGRESS_COLLECTION = "route_progress";

export const useVehicleRoute = (vehicleData: {
    vehicle_id: string;
    coordinates: Coordinates;
    driveGear: DriveGear;
}[]) => {
    const VEHICLE_COLLECTION_INSTANCE = useMemo(() => (
        db.collection(VEHICLE_ROUTE_PROGRESS_COLLECTION)
    ), []);
    const [routeWayPoints, setRouteWayPoints] = useState<{
        [keys: string]: google.maps.LatLngLiteral[]
    }>({});

    useEffect(() => {
        const unsubscribeFromVehicleRouteProgress = (
            vehicleData.length > 0
                ? VEHICLE_COLLECTION_INSTANCE
                    .where(firebase.firestore.FieldPath.documentId(), "in", vehicleData.map(({ vehicle_id }) => vehicle_id))
                : VEHICLE_COLLECTION_INSTANCE
        ).onSnapshot((querySnapshot) => {
            const aggregatedRouteWayPoints: {
                    [keys: string]: google.maps.LatLngLiteral[]
                } = {};

            // Listens to all the vehicle route updates
            querySnapshot.forEach((doc) => {
                const progress = snapshotToProtobuf(
                    doc,
                    RouteProgress.deserializeBinary,
                )?.toObject();

                // Checks if the vehicle route is active, and has waypoints
                if (Number.isFinite(progress.percent)
                        && progress.percent < 1
                        && progress.status === RouteProgressStatus.RUNNING
                ) {
                    // If the vehicle has waypoints, and is running set it
                    // if the vehicle is running and previously had waypoints
                    // but the current waypoints is 0
                    // (this sometimes happen in development),
                    // we use the previous waypoint so the routes dont flicker
                    if (progress.waypoints?.waypointsList
                            && progress.waypoints?.waypointsList.length > 0) {
                        aggregatedRouteWayPoints[doc.id] = (progress.waypoints?.waypointsList
                            .map(({ coordinate }) => (coordinate ? ({
                                lat: coordinate.latitude,
                                lng: coordinate.longitude,
                            }) : undefined))
                            .filter((c) => !!c) || []) as google.maps.LatLngLiteral[];
                    }
                }

                setRouteWayPoints((prevData) => ({ ...prevData, ...aggregatedRouteWayPoints }));
            });
        });

        return () => {
            unsubscribeFromVehicleRouteProgress();
        };
    }, []);

    return vehicleData
        // Filters through the vehicles with valid statuses (forward, reverse, neutral and unknown)
        // then maps the data out, but also adds the current cooordinate
        // to the route data to ensure the line is drawn from the vehicle
        .filter(({ vehicle_id, driveGear }) => (
            routeWayPoints[vehicle_id]?.length > 0
            && [
                DriveGear.FORWARD,
                DriveGear.REVERSE,
                DriveGear.NEUTRAL,
                DriveGear.UNKNOWN,
            ].includes(driveGear)
        ))
        .map(({ vehicle_id, coordinates }) => ({
            vehicleID: vehicle_id,
            routeData: [
                convertLatLng(coordinates),
                ...routeWayPoints[vehicle_id],
            ],
            strokeColor: BLUE_300,
            strokeOpacity: 0.4,
            strokeWeight: 5.0,
        }));
};
